import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { SlidersHorizontal, PackageX } from "lucide-react";

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ products: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("default");
  const [inStockOnly, setInStockOnly] = useState(params.get("in_stock") === "1");

  const search = params.get("search") || "";
  const department = params.get("department") || "";
  const category = params.get("category") || "";
  const brand = params.get("brand") || "";
  const featured = params.get("featured") === "1";
  const page = parseInt(params.get("page") || "1", 10);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (department) q.set("department", department);
    if (category) q.set("category", category);
    if (brand) q.set("brand", brand);
    if (featured) q.set("featured", "true");
    if (inStockOnly) q.set("in_stock", "true");
    if (sort !== "default") q.set("sort", sort);
    q.set("page", page);
    q.set("limit", "24");
    axios.get(`${API}/products?${q.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ products: [], total: 0 }))
      .finally(() => setLoading(false));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [search, department, category, brand, featured, inStockOnly, sort, page]);

  const title = search ? `Résultats pour « ${search} »` : brand || category || department || (featured ? "Offres du moment" : "Tous les produits");
  const totalPages = Math.ceil(data.total / 24);

  const setPage = (p) => {
    const np = new URLSearchParams(params);
    np.set("page", p);
    setParams(np);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-head text-3xl font-extrabold text-slate-900" data-testid="catalog-title">{title}</h1>
          <p className="mt-1 text-sm text-slate-500" data-testid="catalog-count">{data.total} produit{data.total > 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <Checkbox data-testid="instock-filter" checked={inStockOnly} onCheckedChange={(v) => setInStockOnly(!!v)} />
            En stock uniquement
          </label>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-44" data-testid="sort-select">
              <SlidersHorizontal className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Pertinence</SelectItem>
              <SelectItem value="price_asc">Prix croissant</SelectItem>
              <SelectItem value="price_desc">Prix décroissant</SelectItem>
              <SelectItem value="name">Nom (A-Z)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-4">
              <Skeleton className="h-48 w-full" />
              <Skeleton className="mt-4 h-4 w-3/4" />
              <Skeleton className="mt-2 h-6 w-1/2" />
            </div>
          ))}
        </div>
      ) : data.products.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 py-24 text-slate-400">
          <PackageX className="h-16 w-16" />
          <p className="text-lg">Aucun produit trouvé.</p>
          <Button variant="outline" onClick={() => setParams({})}>Réinitialiser</Button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {data.products.map((p) => <ProductCard key={p.sku} product={p} />)}
          </div>

          {totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2">
              <Button variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="prev-page">Précédent</Button>
              <span className="px-4 text-sm text-slate-600">Page {page} / {totalPages}</span>
              <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="next-page">Suivant</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
